import type { FeatureFlagKey } from "@regrello/feature-flags-api";
import type { ConditionOperator, ViewFilterModifier } from "@regrello/graphql-api";

/** A type that describes the ways in which a __single-select__ value can be filtered. */
export type RegrelloFilterDefinition_SingleSelectValue<T> = {
  type: "singleSelect";
  options:
    | {
        type: "default";
        values: Array<{ id: number; label: string; value: T }>;
      }
    | {
        /**
         * Represents a party field that can only have one value. This was introduced to support the
         * Workflow Owner default field. All other party fields are multi-valued as of this writing.
         */
        type: "party";
      }
    | { type: "workflowTemplate" }
    | {
        /** Represents a workflow template object identified and unique by its blueprint UUID. */
        type: "blueprintUuid";
      };

  filter:
    | { operator: "IS_NONE_OF"; values: T[]; modifier?: ViewFilterModifier }
    | { operator: "IS_ANY_OF"; values: T[]; modifier?: ViewFilterModifier };
};

export const regrelloFilterOperators_SingleSelectValue: Record<
  RegrelloFilterDefinition_SingleSelectValue<unknown>["filter"]["operator"],
  true | FeatureFlagKey
> = {
  IS_ANY_OF: true,
  IS_NONE_OF: true,
};

export function getRegrelloDefaultFilterDefinitionSingleSelectValue<T>(
  options?: RegrelloFilterDefinition_SingleSelectValue<T>["options"],
): RegrelloFilterDefinition_SingleSelectValue<T> {
  return {
    type: "singleSelect",
    options: options ?? { type: "default", values: [] },
    filter: {
      operator: "IS_ANY_OF",
      values: [],
    },
  };
}
export function getRegrelloFilterDefinitionSingleSelectValue(
  operator: ConditionOperator,
  values: string[],
  allowedValues: Array<{
    id: number;
    label: string;
    value: string;
  }>,
): RegrelloFilterDefinition_SingleSelectValue<unknown> | undefined {
  switch (operator) {
    case "IS_ANY_OF":
    case "IS_NONE_OF":
      return {
        type: "singleSelect",
        options: {
          type: "default",
          values: allowedValues,
        },
        filter: {
          operator: operator,
          values: values.map((value, index) => {
            return { id: index, label: value, value: value };
          }),
        },
      };
    default:
      console.warn("Unrecognized operator for single select filter:", operator);
      return undefined;
  }
}
